import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { seguimientoApi } from "./api";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { Badge } from "./ui";
import { useToast } from "@/app/providers/Toaster";
import {
  AUDIENCE_LABELS,
  RESULT_LABELS,
  RESULT_OPTIONS,
  RESULT_TONE,
} from "./labels";
import type { ValidationAudience, ValidationModule, ValidationResult } from "./types";

const AUDIENCE_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "Todos" },
  { value: "productor", label: "Productores" },
  { value: "artista", label: "Artistas" },
  { value: "proveedor", label: "Proveedores" },
];

const AUDIENCE_ORDER: ValidationAudience[] = ["productor", "artista", "proveedor"];

export function ValidacionPane() {
  const [audience, setAudience] = useState("");
  const qc = useQueryClient();
  const { push } = useToast();

  const listQuery = useQuery({
    queryKey: ["seg", "validations", { audience }],
    queryFn: () => seguimientoApi.validations(audience ? { audience } : undefined),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["seg", "validations"] });

  const setResult = useMutation({
    mutationFn: async (args: { module: ValidationModule; result: ValidationResult }) => {
      const base = args.module.rows.find((r) => r.eventId == null);
      if (base) return seguimientoApi.updateValidation(base.id, { result: args.result });
      return seguimientoApi.createValidation({
        audience: args.module.audience,
        moduleName: args.module.name,
        result: args.result,
      });
    },
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["seg", "metrics"] });
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo guardar." }),
  });

  const modules = listQuery.data ?? [];
  const grouped = AUDIENCE_ORDER.map((aud) => ({
    audience: aud,
    items: modules.filter((m) => m.audience === aud),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
          className="nx-input w-auto"
        >
          {AUDIENCE_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="text-2xs text-fg-subtle">
          Catálogo de {modules.length} módulos — alimenta el % de validación de Métricas.
        </span>
        <button
          onClick={() => listQuery.refetch()}
          className="nx-btn-secondary ml-auto"
          disabled={listQuery.isFetching}
        >
          <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </div>

      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando validaciones…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : (
        grouped.map((g) => (
          <div key={g.audience} className="nx-card overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border bg-bg-surface">
              <h2 className="text-sm font-medium text-fg">{AUDIENCE_LABELS[g.audience]}</h2>
            </div>
            <div className="divide-y divide-border">
              {g.items.map((mod) => (
                <div
                  key={`${mod.audience}-${mod.name}`}
                  className="flex flex-wrap items-center gap-3 px-4 py-2.5"
                >
                  <span className="text-sm text-fg flex-1 min-w-[180px]">{mod.name}</span>
                  <Badge tone={RESULT_TONE[mod.result]}>{RESULT_LABELS[mod.result]}</Badge>
                  {mod.recordCount > 1 && (
                    <span className="text-2xs text-fg-subtle">{mod.recordCount} registros</span>
                  )}
                  <select
                    value={mod.result}
                    onChange={(e) =>
                      setResult.mutate({ module: mod, result: e.target.value as ValidationResult })
                    }
                    disabled={setResult.isPending}
                    className="nx-input w-auto text-xs"
                  >
                    {RESULT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
